import React, { useCallback, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useCompanyBranding } from "../lib/branding";
import { usePlatformAccess } from "../hooks/usePlatformAccess";
import OrgSwitcher from "./OrgSwitcher";
import NotificationBell from "./NotificationBell";
import AssistantLauncher from "./assistant/AssistantLauncher";

const MOBILE_QUERY = "(max-width: 640px)";

/** Single source of truth for "is this a phone-width viewport".
 *  Page-level actions are rendered EITHER in the topbar (desktop) OR in their
 *  own full-width bar underneath it (mobile) — never both, so every
 *  data-testid stays unique. */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", onChange);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

function NavItem({ to, label, icon, onNavigate }) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      data-testid={`nav-${to.replace(/^\//,"")}`}
      className={({isActive}) => isActive ? "active" : ""}
    >
      <span style={{ width: 16, textAlign: "center" }} aria-hidden="true">{icon}</span> {label}
    </NavLink>
  );
}

export default function Layout({ title, actions, children }) {
  const { t } = useTranslation();
  const { me, currentOrg, signOut, orgId } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);
  const branding = useCompanyBranding(orgId);
  const companyName = branding?.name || currentOrg?.name || "";
  // Phase 2D.1B — the platform console entry point exists only for an active
  // platform super administrator. Every other user (owner, manager, sales) sees
  // exactly the navigation they saw before.
  const { isPlatformAdmin } = usePlatformAccess();
  // Phase 2D.2 checkpoint 2B — "Потребители" exists for an Owner and a Manager.
  // A Sales user does not see it, and the backend refuses the endpoints behind it
  // for them anyway: hiding a link is never the control.
  const canManageUsers = currentOrg?.role === "owner" || currentOrg?.role === "manager";

  const closeNav = useCallback(() => setNavOpen(false), []);

  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setNavOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  return (
    <div className="qw-shell">
      {navOpen && (
        <div
          className="qw-nav-backdrop"
          onClick={closeNav}
          data-testid="mobile-nav-backdrop"
          aria-hidden="true"
        />
      )}
      <aside id="qw-sidebar" className={`qw-sidebar${navOpen ? " open" : ""}`}>
        <div className="brand">
          <span className="brand-dot" aria-hidden="true" />
          OFFERTRACE
          <button
            type="button"
            className="qw-sidebar-close"
            onClick={closeNav}
            aria-label={t("nav.close_menu")}
            data-testid="mobile-nav-close"
          >
            <X size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
        <nav className="nav" aria-label={t("nav.primary")}>
          <NavItem to="/dashboard" label={t("nav.dashboard")} icon={"■"} onNavigate={closeNav} />
          <NavItem to="/quotations" label={t("nav.quotations")} icon={"¶"} onNavigate={closeNav} />
          <NavItem to="/customers" label={t("nav.customers")} icon={"◉"} onNavigate={closeNav} />
          {canManageUsers && (
            <NavItem
              to="/organization/users"
              label={t("nav.users")}
              icon={"☺"}
              onNavigate={closeNav}
            />
          )}
          <NavItem to="/notifications" label={t("nav.notifications")} icon={"◉"} onNavigate={closeNav} />
          <NavItem to="/profile" label={t("nav.profile")} icon={"◍"} onNavigate={closeNav} />
          <NavItem to="/settings" label={t("nav.settings")} icon={"⚙"} onNavigate={closeNav} />
          {isPlatformAdmin && (
            <NavItem
              to="/platform-admin"
              label={t("platform.nav_link")}
              icon={"◆"}
              onNavigate={closeNav}
            />
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="user-card">
            {companyName && (
              <div className="qw-org-brand" data-testid="org-brand">
                {branding?.logo_url ? (
                  <img
                    src={branding.logo_url}
                    alt=""
                    className="qw-org-brand-logo"
                    data-testid="org-brand-logo"
                  />
                ) : (
                  <span className="qw-org-brand-initial" aria-hidden="true">
                    {companyName.trim().charAt(0)}
                  </span>
                )}
                <span className="qw-org-brand-name" data-testid="org-brand-name">
                  {companyName}
                </span>
              </div>
            )}
            <div className="name">{me?.full_name || me?.email}</div>
            <div className="role">{companyName}</div>
            <button className="signout" onClick={async () => { await signOut(); nav("/login"); }} data-testid="sign-out-btn">{t("auth.sign_out")}</button>
          </div>
        </div>
      </aside>
      <div className="qw-main">
        <div className="qw-topbar">
          <button
            type="button"
            className="qw-nav-toggle"
            onClick={() => setNavOpen((v) => !v)}
            aria-label={t("nav.menu")}
            aria-expanded={navOpen}
            aria-controls="qw-sidebar"
            data-testid="mobile-nav-toggle"
          >
            <Menu size={18} strokeWidth={2} aria-hidden="true" />
          </button>
          <div className="title" title={typeof title === "string" ? title : undefined}>{title}</div>
          <div className="actions">
            <OrgSwitcher />
            {/* Phase 4A.5A — the Assistant lives in the authenticated shell
                only, so it inherits <Guarded> and the shared API client's
                Authorization / X-Organization-ID headers unchanged. */}
            <AssistantLauncher />
            <NotificationBell />
            {!isMobile && actions}
          </div>
        </div>
        {isMobile && actions && (
          <div className="qw-page-actions" data-testid="page-actions-bar">{actions}</div>
        )}
        <div className="qw-content">{children}</div>
      </div>
    </div>
  );
}
